/**
 * MetricCard — DeCarbonIQ Design System
 * Standard KPI metric card. White surface, slate border, rounded-xl.
 *
 * Props:
 *   label      — metric name (shown as uppercase label)
 *   value      — primary numeric/text value
 *   unit       — unit string (shown below value, small)
 *   sub        — secondary description line
 *   delta      — % change (negative = good for emissions; positive = increase)
 *   deltaGoodIfNegative — if true, negative delta = green (default: true)
 *   icon       — lucide icon component
 *   iconBg     — bg color class for icon container (default "bg-slate-100")
 *   color      — tailwind text color class for the value (default "text-slate-900")
 *   className  — extra outer classes
 *   testid     — data-testid value
 */
import React from "react";
import { TrendingDown, TrendingUp } from "lucide-react";

export function MetricCard({
  label,
  value,
  unit,
  sub,
  delta,
  deltaGoodIfNegative = true,
  icon: Icon,
  iconBg = "bg-slate-100",
  color = "text-slate-900",
  className = "",
  testid,
}) {
  const isGoodDelta = deltaGoodIfNegative ? delta < 0 : delta > 0;

  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`}
      data-testid={testid}
    >
      {Icon && (
        <div className={`w-8 h-8 ${iconBg} rounded-lg flex items-center justify-center mb-3`}>
          <Icon size={15} className="text-slate-500" />
        </div>
      )}
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1.5">
        {label}
      </p>
      <div className="flex items-end gap-1.5">
        <span className={`text-2xl font-bold tabular-nums ${color}`}>{value ?? "—"}</span>
        {unit && <span className="text-sm text-slate-400 mb-0.5">{unit}</span>}
      </div>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      {delta !== undefined && delta !== null && (
        <p
          className={`text-xs mt-1.5 flex items-center gap-1 font-medium ${
            isGoodDelta ? "text-emerald-600" : "text-red-500"
          }`}
        >
          {isGoodDelta ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
          {delta > 0 ? "+" : ""}
          {typeof delta === "number" ? delta.toFixed(1) : delta}%
        </p>
      )}
    </div>
  );
}

export default MetricCard;
